import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  MdGroups,
  MdHome,
  MdLeaderboard,
  MdLogout,
  MdMenu,
  MdFactCheck,
  MdQrCode,
  MdQrCodeScanner,
  MdSportsSoccer,
} from "react-icons/md";
import { LogoutButton } from "../auth/LogoutButton";
import { BrandButton } from "../ui/BrandButton";
import { TrackedImage } from "../ui/TrackedImage";
import { useAuth } from "../../context/AuthContext";
import { useTrackedImagePreload } from "../../hooks/useTrackedImagePreload";
import { useViewportSize } from "../../hooks/useViewportSize";
import logoBlueLong from "../../assets/brand/logos/AzulOscuroLargo.png";
import logoPencaBlue from "../../assets/brand/penca/LogoPencaAzulHorizontal.png";
import pelotaIcon from "../../assets/brand/penca/pelota.webp";
import adminBackground from "../../assets/brand/backgrounds/DG2.webp";
import adminBackgroundMobile from "../../assets/brand/backgrounds/DG2Mobile.webp";

const SIDEBAR_STORAGE_KEY = "penca_osu_admin_sidebar_state";

function getInitialSidebarState() {
  if (typeof window === "undefined") {
    return "expanded";
  }

  const storedValue = window.sessionStorage.getItem(SIDEBAR_STORAGE_KEY);
  return storedValue === "collapsed" ? "collapsed" : "expanded";
}

export function AdminShell() {
  const { user } = useAuth();
  const location = useLocation();
  const { isCompactDesktop } = useViewportSize();
  useTrackedImagePreload(adminBackground);
  const [sidebarState, setSidebarState] = useState(getInitialSidebarState);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const isAdminUser = user?.role?.name === "admin" || user?.role?.name === "superadmin";

  const navigationItems = useMemo(
    () => [
      {
        to: "/home",
        label: "Inicio",
        icon: MdHome,
      },
      {
        to: "/ranking",
        label: "Ranking",
        icon: MdLeaderboard,
      },
      {
        to: "/predictions",
        label: "Predicciones",
        icon: MdSportsSoccer,
      },
      {
        to: "/scan-qr",
        label: "Escanear QR",
        icon: MdQrCodeScanner,
      },
      ...(isAdminUser
        ? [
            {
              to: "/admin/users",
              label: "Usuarios",
              icon: MdGroups,
            },
            {
              to: "/admin/qr",
              label: "QR tnuot",
              icon: MdQrCode,
            },
            {
              to: "/admin/puntajes",
              label: "Puntajes",
              icon: MdFactCheck,
            },
          ]
        : []),
    ],
    [isAdminUser],
  );

  useEffect(() => {
    window.sessionStorage.setItem(SIDEBAR_STORAGE_KEY, sidebarState);
  }, [sidebarState]);

  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  const isCollapsed = sidebarState === "collapsed";
  const toggleSidebarState = () => {
    setSidebarState((current) => (current === "collapsed" ? "expanded" : "collapsed"));
  };

  const openMobileSidebar = () => setIsMobileSidebarOpen(true);
  const closeMobileSidebar = () => setIsMobileSidebarOpen(false);

  const shellSidebar = (
    <aside
      className={`fixed inset-y-4 left-4 z-40 flex w-[16.5rem] flex-col overflow-hidden rounded-[2rem] border border-brand-secondary/15 bg-white/95 shadow-soft backdrop-blur transition-transform duration-300 lg:shrink-0 lg:transition-[width,box-shadow,transform] lg:duration-300 lg:ease-out ${
        isMobileSidebarOpen ? "translate-x-0" : "-translate-x-[calc(100%+1rem)]"
      } lg:sticky lg:top-4 lg:z-auto lg:translate-x-0 ${isCollapsed ? "lg:w-[5.5rem]" : "lg:w-[16.5rem]"} max-sm:shadow-none`}
      aria-label="Menú principal"
    >
      <div className="flex items-center border-b border-brand-secondary/10 px-4 py-4 lg:px-5">
        <BrandButton
          type="button"
          onClick={() => {
            if (window.innerWidth < 1024) {
              closeMobileSidebar();
              return;
            }

            toggleSidebarState();
          }}
          variant="ghost"
          radius="2xl"
          size="icon"
          motion="subtle"
          className="h-14 w-full transition-opacity hover:!bg-transparent hover:opacity-80"
          aria-label="Abrir o cerrar menú principal"
        >
          <span className="relative flex h-full w-full items-center justify-center">
            <TrackedImage
              src={logoPencaBlue}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 m-auto h-full w-auto max-w-full object-contain object-center transition-all duration-300 ease-out ${
                isCollapsed ? "scale-95 opacity-0 lg:-translate-x-2" : "scale-100 opacity-100"
              }`}
            />
            <TrackedImage
              src={pelotaIcon}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 m-auto h-10 w-10 object-contain object-center transition-all duration-300 ease-out ${
                isCollapsed ? "scale-100 opacity-100" : "scale-95 opacity-0 lg:translate-x-2"
              }`}
            />
          </span>
        </BrandButton>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4 pt-8 lg:px-4 lg:pt-10">
        <div className="grid gap-3">
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.to;

            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => {
                  if (window.innerWidth < 1024) {
                    closeMobileSidebar();
                  }
                }}
                className={[
                  "flex items-center gap-3 rounded-[1.25rem] px-2 py-2 transition-colors lg:transition-[padding,gap,background-color,color,transform] lg:duration-300 lg:ease-out",
                  isActive
                    ? "bg-brand-primary text-white shadow-sm"
                    : "text-slate-600 hover:bg-brand-primary/5 hover:text-brand-primary",
                  isCollapsed ? "lg:justify-center lg:px-0 lg:gap-0" : "",
                ].join(" ")}
                title={item.label}
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center text-lg ${
                    isActive ? "text-white" : "text-brand-primary"
                  }`}
                >
                  <item.icon aria-hidden="true" className="h-5 w-5" />
                </span>
                <span
                  className={`min-w-0 overflow-hidden whitespace-nowrap transition-all duration-300 ease-out ${
                    isCollapsed ? "lg:max-w-0 lg:opacity-0 lg:translate-x-2" : "lg:max-w-[12rem] lg:opacity-100"
                  }`}
                >
                  <span className="block text-sm font-semibold">{item.label}</span>
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      <div
        className={`mt-auto flex items-center justify-center border-t border-brand-secondary/10 py-4 ${
          isCollapsed ? "px-2 lg:px-2" : "px-4 lg:px-5"
        }`}
      >
        <LogoutButton
          className={
            isCollapsed
              ? "mx-auto flex h-11 w-11 shrink-0 items-center justify-center !gap-0 overflow-hidden px-0 leading-none"
              : "w-full"
          }
          radius={isCollapsed ? "full" : "xl"}
          size={isCollapsed ? "icon" : "lg"}
          aria-label="Cerrar sesión"
          title="Cerrar sesión"
        >
          <MdLogout aria-hidden="true" className="h-5 w-5" />
          {!isCollapsed ? (
            <span className="overflow-hidden whitespace-nowrap transition-all duration-300 ease-out lg:max-w-[10rem] lg:opacity-100">
              <span className="text-sm font-semibold">Cerrar sesión</span>
            </span>
          ) : null}
        </LogoutButton>
      </div>
    </aside>
  );

  return (
    <div
      className="admin-background min-h-[100dvh] overflow-x-hidden bg-cover bg-center bg-no-repeat bg-fixed text-slate-900 antialiased max-sm:fixed max-sm:inset-0 max-sm:h-[100svh] max-sm:overflow-hidden"
      style={{
        "--admin-background-image": `url(${adminBackground})`,
        "--admin-background-mobile-image": `url(${adminBackgroundMobile})`,
      }}
    >
      <div className={`flex min-h-[100dvh] ${isCompactDesktop ? "gap-4 p-4" : "gap-4 p-4"} max-sm:h-full max-sm:min-h-0 max-sm:gap-3 max-sm:p-3.5`}>
        {shellSidebar}

        {isMobileSidebarOpen ? (
          <BrandButton
            type="button"
            onClick={closeMobileSidebar}
            variant="ghost"
            radius="none"
            size="icon"
            motion="none"
            className="fixed inset-0 z-30 bg-brand-primary/25 backdrop-blur-[1px] lg:hidden"
            aria-label="Cerrar menú de administración"
          />
        ) : null}

        <main
          className={`flex min-w-0 flex-1 min-h-0 flex-col ${
            isCompactDesktop ? "gap-3" : "gap-3"
          } max-sm:h-full max-sm:overflow-hidden lg:h-[calc(100dvh-2rem)] lg:overflow-hidden`}
        >
          <header
            className={`flex items-center rounded-[2rem] border border-brand-secondary/15 bg-white/95 px-4 shadow-soft backdrop-blur ${
              isCompactDesktop ? "h-[5.25rem] sm:h-[5.5rem] sm:px-4" : "h-[5.25rem] sm:h-[5.5rem] sm:px-5"
            } max-sm:shadow-none`}
          >
            <div className="flex w-full items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <BrandButton
                  type="button"
                  onClick={openMobileSidebar}
                  variant="outline"
                  radius="full"
                  size="icon"
                  motion="subtle"
                  className="h-10 w-10 border-slate-200 p-1.5 text-brand-primary shadow-sm lg:hidden"
                  aria-label="Abrir menú principal"
                >
                  <MdMenu aria-hidden="true" className="h-6 w-6" />
                </BrandButton>

                <div className="min-w-0">
                  <h1 className="truncate font-display text-2xl text-brand-primary sm:text-3xl lg:text-[2.7rem] p-1">
                    Hola, {user?.name}
                  </h1>
                </div>
              </div>
              <TrackedImage
                src={logoBlueLong}
                alt="Organización Sionista del Uruguay"
                className={`hidden h-auto w-full object-contain sm:block ${
                  isCompactDesktop ? "sm:max-w-[210px] lg:max-w-[210px]" : "sm:max-w-[220px] lg:max-w-[235px]"
                }`}
              />
            </div>
          </header>

          <section
            className={`rounded-[2rem] border border-brand-secondary/15 bg-white/95 shadow-soft backdrop-blur lg:min-h-0 lg:flex-1 lg:overflow-hidden ${
              isCompactDesktop ? "p-3.5 sm:p-4 lg:p-4" : "p-3.5 sm:p-4 lg:p-4"
            } max-sm:min-h-0 max-sm:flex-1 max-sm:shadow-none`}
          >
            <div className="pr-1 lg:h-full lg:min-h-0 lg:overflow-y-auto max-sm:h-full max-sm:min-h-0 max-sm:overflow-y-auto">
              <Outlet />
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
